import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Stack, Button, IconButton } from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { toast } from 'react-toastify'
import InvoiceAccordionSummary from 'components/customer-details/content/invoices/InvoiceAccordionSummary'
import T from 'T'

const MAX_FILE_SIZE_MB = 2
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024
const MAX_TOTAL_SIZE_MB = 3
const MAX_TOTAL_SIZE = MAX_TOTAL_SIZE_MB * 1024 * 1024

const AttachmentsList = ({ attachments, invoice, onAttachmentsChange }) => {
  const fileInputRef = useRef(null)
  const totalAttachmentSize = attachments.reduce((sum, att) => sum + (att.size || 0), 0)
  const hasReachedTotalLimit = totalAttachmentSize >= MAX_TOTAL_SIZE

  const formatFileSize = bytes => {
    const normalizedBytes = Math.max(0, Number.isFinite(bytes) ? bytes : 0)
    if (normalizedBytes === 0) return '0 B'
    if (normalizedBytes < 1024) return `${normalizedBytes} B`
    if (normalizedBytes < 1024 * 1024) return `${(normalizedBytes / 1024).toFixed(1)} KB`
    return `${(normalizedBytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleFileSelect = event => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(T.FILE_TOO_LARGE.replace('{fileName}', file.name).replace('{maxSize}', MAX_FILE_SIZE_MB.toString()))
        return false
      }
      return true
    })

    let runningTotalSize = totalAttachmentSize
    const attachmentsWithinLimit = []

    validFiles.forEach(file => {
      if (runningTotalSize + file.size > MAX_TOTAL_SIZE) {
        toast.error(T.TOTAL_ATTACHMENT_SIZE_EXCEEDED.replace('{fileName}', file.name).replace('{maxSize}', MAX_TOTAL_SIZE_MB.toString()))
        return
      }

      attachmentsWithinLimit.push({
        id: `file-${crypto.randomUUID()}`,
        name: file.name,
        type: 'file',
        isInvoice: false,
        size: file.size,
        file,
      })
      runningTotalSize += file.size
    })

    if (attachmentsWithinLimit.length > 0) {
      onAttachmentsChange([...attachments, ...attachmentsWithinLimit])
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveAttachment = attachmentId => {
    onAttachmentsChange(attachments.filter(att => att.id !== attachmentId))
  }

  const handleReattachInvoice = () => {
    const { invoiceNumber } = invoice
    onAttachmentsChange([
      {
        id: `invoice-${invoiceNumber}`,
        name: `Invoice-${invoiceNumber}.pdf`,
        type: 'invoice',
        isInvoice: true,
        size: 0,
      },
      ...attachments,
    ])
  }

  const handleAddAttachmentClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <Box mb={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachFileIcon fontSize="small" />
          <Typography variant="caption" color="text.secondary">
            {`${T.ATTACHED_FILES} (${attachments.length})`}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {`${formatFileSize(totalAttachmentSize)} / ${MAX_TOTAL_SIZE_MB} MB`}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {attachments.map(attachment => (
          <React.Fragment key={attachment.id}>
            {attachment.isInvoice ? (
              <Box p={1} borderBottom="1px solid" borderColor="divider">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <InvoiceAccordionSummary invoice={invoice} isViewOnly expandable={false} />

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {T.PDF}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                      color="error"
                      title="Remove invoice attachment"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Box px={2} py={1} borderBottom="1px solid" borderColor="divider">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <InsertDriveFileIcon color="action" />
                    <Box>
                      <Typography variant="subtitle2" noWrap sx={{ maxWidth: 300 }}>
                        {attachment.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(attachment.size)}
                      </Typography>
                    </Box>
                  </Stack>

                  <IconButton size="small" onClick={() => handleRemoveAttachment(attachment.id)} color="error" title="Remove attachment">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            )}
          </React.Fragment>
        ))}

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddAttachmentClick}
            size="small"
            disabled={hasReachedTotalLimit}
          >
            {T.ADD_ATTACHMENT}
          </Button>

          {!attachments.some(att => att.isInvoice) && (
            <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={handleReattachInvoice} size="small" color="primary">
              {T.ATTACH_INVOICE}
            </Button>
          )}
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
        />
      </Stack>
    </Box>
  )
}

AttachmentsList.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      isInvoice: PropTypes.bool.isRequired,
      size: PropTypes.number.isRequired,
      file: PropTypes.object,
    })
  ).isRequired,
  invoice: PropTypes.object.isRequired,
  onAttachmentsChange: PropTypes.func.isRequired,
}

export default AttachmentsList
